import fingerprint from '@fingerprintjs/fingerprintjs';

import {ONE_SECOND_MS} from '@/shared/consts/time';
import {delay} from '@/shared/lib/delay';
import {getCampaign} from '@/shared/lib/localStorage/campaign';
import {
  getConfig,
  setConfig,
} from '@/shared/lib/localStorage/internalAnalytics';
import {notNullValues} from '@/shared/lib/object';

import {init as initRequest} from './api/init';
import {sendEvents as sendEventsRequest} from './api/sendEvents';
import {supportEvent} from './lib/supportEvent';
import {EventItem, Options, SharedState, State, SupportEvents} from './types';

const NOT_INITIALIZED_EVENT_SIZE = 100;
const TRY_INIT_INTERVAL = 3 * ONE_SECOND_MS;
const SEND_INTERVAL = 3 * ONE_SECOND_MS;
const TRY_INIT_ATTEMPTS = 3;
const SEND_EVENTS_MAX_BULK_SIZE = 10;

const defaultState: State = {
  initStatus: 'not_initialized',
  initPromise: null,
  sendInterval: SEND_INTERVAL,
  initializeAttempts: 0,
  scheduleTimeoutId: undefined,
  events: [],
  options: {
    enable: true,
    sendInterval: SEND_INTERVAL,
    clearEvents: false,
  },
  abtests: [],
};

let state: State = defaultState;

function makeState(options?: Options): State {
  const stateOptions = {
    ...defaultState.options,
    ...options,
  };

  return {
    ...defaultState,
    events: stateOptions?.clearEvents ? [] : defaultState.events,
    sendInterval: stateOptions.sendInterval,
    options: stateOptions,
  };
}

export async function init(options?: Options): Promise<SharedState> {
  state = makeState(options);
  if (state.initStatus === 'initialized' || !state.options.enable) {
    return {
      abtests: state.abtests,
    };
  }

  if (!state.initPromise) {
    state.initPromise = runInitAnalytics();
  }

  return state.initPromise;
}

export async function track(
  name: SupportEvents,
  {ctx_id}: Pick<EventItem, 'ctx_id'> = {}
) {
  if (!supportEvent(name)) {
    return;
  }

  state.events.push({
    n: `web.${name}`,
    ts: Date.now(),
    ctx_id,
  });

  const notInitializedOverSizeEvents =
    state.events.length - NOT_INITIALIZED_EVENT_SIZE;
  if (state.initStatus !== 'initialized' && notInitializedOverSizeEvents > 0) {
    state.events.splice(0, notInitializedOverSizeEvents);
  }

  scheduleSendEvents();
}

function scheduleSendEvents() {
  if (state.scheduleTimeoutId) {
    return;
  }

  state.scheduleTimeoutId = setTimeout(async () => {
    try {
      await sendEvents();

      if (state.events.length > 0) {
        scheduleSendEvents();
      }
    } catch {
      // noop
    } finally {
      state.scheduleTimeoutId = undefined;
    }
  }, state.sendInterval);
}

async function sendEvents() {
  if (!state.events.length || state.initStatus !== 'initialized') {
    return;
  }

  const pushEvents = state.events.splice(0, SEND_EVENTS_MAX_BULK_SIZE);
  try {
    await sendEventsRequest(pushEvents, {
      uuid: state.uuid,
    });
  } catch (error) {
    state.events.unshift(...pushEvents);
    throw error;
  }
}

async function runInitAnalytics(): Promise<SharedState> {
  if (state.initializeAttempts >= TRY_INIT_ATTEMPTS) {
    state.initStatus = 'error';
    throw new Error('Reached init attempts');
  }

  state.initStatus = 'initializing';

  const storedConfig = getConfig();
  if (storedConfig) {
    state.uuid = storedConfig.uuid;
    state.abtests = storedConfig.abtests ?? [];
    state.initStatus = 'initialized';
    return {
      abtests: state.abtests,
    };
  }

  const campaign = getCampaign();
  try {
    const {uuid, abtests} = await initRequest(
      notNullValues({
        uuid: await getVisitorId(),
        campaign_info: campaign?.value,
      })
    );

    state.uuid = uuid;
    state.abtests = abtests;
    state.initStatus = 'initialized';
    setConfig({
      uuid,
      abtests,
    });
    scheduleSendEvents();
  } catch {
    state.initializeAttempts++;
    await delay(TRY_INIT_INTERVAL);
    return runInitAnalytics();
  }

  return {
    abtests: state.abtests,
  };
}

async function getVisitorId() {
  if (!state.visitorUuid) {
    const fp = await fingerprint.load();
    const {visitorId} = await fp.get();
    state.visitorUuid = visitorId;
  }

  return state.visitorUuid;
}
